import React, { FC, useEffect, useState } from 'react'
import { Divider, Spinner, csx, Text } from '@vtex/admin-ui'
import last from 'lodash/last'
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import { FormattedMessage } from 'react-intl'

import AddressBar from './AddressBar/AddressBarContainer'
import {
  isPickUpAvailable,
  isDeliveryAvailable,
} from '@/checkout-instore-legacy/cart/simulation/utils'
import { excludeCheckInFromSlas } from '@/checkout-instore-legacy/utils/checkInUtils'
import {
  extractItemsForPickupPoint,
  extractItemsForDelivery,
} from '@/checkout-instore-legacy/cart/components/ItemShippingInfoAid/utils'
import { stockProductSelector } from '@/checkout-instore-legacy/product/selectors'

import ShippingInfoBox from './ShippingInfoBox'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'

interface ShippingInformation {
  logisticsInfo: LogisticsInfo
  quantity: number
  isLoading: boolean
  skuId: string
  showShippingInformation: boolean
}

const ShippingInformation: FC<ShippingInformation> = ({
  logisticsInfo,
  quantity,
  isLoading,
  skuId,
  showShippingInformation,
}) => {
  function getLogisticsInfoWithMostQuantity(logisticsInfo: LogisticsInfo) {
    const sortedByQuantitylogisticsInfo = sortBy(logisticsInfo, 'quantity')
    return last(sortedByQuantitylogisticsInfo)
  }

  const stock = stockProductSelector(getReduxStore().getState())

  const skuCheckedInStock = stock[skuId]?.checkedInStock ?? null

  const logisticsInfoWithMostQuantity = getLogisticsInfoWithMostQuantity(
    logisticsInfo
  )

  function getDeliveryTableItemsFromLogisticsInfo(
    logisticsInfo: LogisticsInfoItem | undefined
  ) {
    if (isEmpty(logisticsInfo)) {
      return []
    }
    const filteredSLAS = excludeCheckInFromSlas(logisticsInfo?.slas)
    return extractItemsForDelivery(filteredSLAS)
  }

  function getPickUpTableItemsFromLogisticsInfo(
    logisticsInfo: LogisticsInfoItem | undefined
  ) {
    if (isEmpty(logisticsInfo)) {
      return []
    }
    const filteredSLAS = excludeCheckInFromSlas(logisticsInfo?.slas)
    return extractItemsForPickupPoint(filteredSLAS)
  }

  const deliveryItems = isDeliveryAvailable(logisticsInfo, quantity)
    ? getDeliveryTableItemsFromLogisticsInfo(logisticsInfoWithMostQuantity)
    : []

  const pickUpItems = isPickUpAvailable(logisticsInfo, quantity)
    ? getPickUpTableItemsFromLogisticsInfo(logisticsInfoWithMostQuantity)
    : []

  const [fakeLoad, setFakeLoad] = useState(false)

  useEffect(() => {
    setFakeLoad(true)
    setTimeout(() => setFakeLoad(false), 300)
  }, [skuId, quantity, showShippingInformation])

  if (isLoading || fakeLoad) {
    return (
      <div className={csx({ flexWrap: 'wrap', marginTop: 6 })}>
        <div
          className={csx({
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#e3e4e6',
            alignItems: 'center',
            padding: '1rem',
          })}
        >
          <AddressBar />
        </div>
        {showShippingInformation && (
          <div
            className={csx({
              marginBottom: '20px',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: '#e3e4e6',
              alignItems: 'center',
              height: '200px',
            })}
          >
            <Spinner
              data-testid="delivery-pickup-skeleton-loading"
              className={csx({ marginLeft: '45%', marginTop: '75px' })}
              size={50}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={csx({ marginBottom: '10px', flexWrap: 'wrap', marginTop: 6 })}
    >
      <div
        className={csx({
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: '#e3e4e6',
          alignItems: 'center',
          padding: '1rem',
        })}
      >
        <AddressBar />
      </div>
      {showShippingInformation && (
        <div
          className={csx({
            marginBottom: '20px',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#e3e4e6',
            alignItems: 'center',
          })}
        >
          <ShippingInfoBox
            items={deliveryItems}
            label="shippingTimeLabelProductPage"
            optionsLabel="deliveryOptionsProductPage"
            unavailableLabel="shippingDeliveryUnavailable"
          />
          <Divider className={csx({ marginLeft: '5%', width: '90%' })} />
          <ShippingInfoBox
            items={pickUpItems}
            label="pickupTimeLabelProductPage"
            optionsLabel="pickupOptionsProductPage"
            unavailableLabel="shippingPickupUnavailable"
          />

          <Divider className={csx({ marginLeft: '5%', width: '90%' })} />
          <div
            className={csx({
              marginLeft: '1.75rem',
              marginTop: '1.25rem',
              flexDirection: 'column',
              width: '70%',
              marginBottom: '1.25rem',
              '> *': {
                display: 'block',
              },
            })}
          >
            <Text className={csx({ fontSize: 14 })}>
              <FormattedMessage id="takeAwayTimeLabelProductPage" />
            </Text>
            {!!skuCheckedInStock && skuCheckedInStock !== 0 ? (
              <Text className={csx({ fontSize: 14, fontWeight: 600 })}>
                <FormattedMessage
                  id="takeAwayQuantityLabelProductPage"
                  values={{ quantity: skuCheckedInStock }}
                />
              </Text>
            ) : (
              <Text className={csx({ fontSize: 14, fontWeight: 600 })}>
                <FormattedMessage id="takeAwaySoldOutLabelProductPage" />
              </Text>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ShippingInformation
